/**
 * Command-dashboard aggregations — the single source of truth for the numbers
 * the operations screen leads with.
 *
 * Everything here is pure: no Dexie, no React and no clock of its own. The
 * dashboard hands in the lists it already loaded and reads the counters back,
 * so the same aggregation the screen renders is the one the healthcheck runs.
 *
 * The rules that already exist are not re-implemented: a vehicle's and a
 * soldier's effective whereabouts come from `./effective-status`, and the
 * calendar key from `./task-filters`.
 */
import { toDateKey } from './task-filters';
import {
  EMPTY_ASSIGNMENT_INDEX,
  activeAssignmentIndex,
  effectiveSoldierAvailability,
  effectiveVehicleStatus,
} from './effective-status';
import type { AssignmentIndex } from './effective-status';
import { isActiveTask, isCompletedTask, normalizeTaskStatus } from '../types/task';
import type { Task } from '../types/task';
import type { TaskTeam } from '../types/assignment';
import type { Soldier, Vehicle } from '../types/fleet';
import type { AuditLogEntry } from '../types/audit';

// ── Tasks ────────────────────────────────────────────────────────────

/**
 * The day's board under the two-state lifecycle: how many tasks are running,
 * how many are closed, and which of the running ones need a manager's eye.
 *
 * The waiting rooms are deliberately absent. "ממתינות לשיבוץ" and "ממתינות
 * לסגירה" were counters over statuses the board no longer keeps; what a
 * dispatcher actually wanted out of the first — a task with nobody on it — is
 * still here as {@link TaskDayKpis.unassigned}, derived from the convoy rather
 * than from a status somebody had to remember to set.
 */
export interface TaskDayKpis {
  /** Every task filed for the day. */
  total: number;
  /** בביצוע — the tasks that are running. */
  inProgress: number;
  /** הושלמו. */
  completed: number;
  cancelled: number;
  /** Still on the board. Under the two-state lifecycle, this is `inProgress`. */
  open: number;
  /** Open tasks marked דחוף. */
  urgent: number;
  /** Open tasks with no vehicle team at all. */
  unassigned: number;
}

/** The tasks filed for one calendar day. */
export function tasksOnDay(tasks: readonly Task[], dateKey: string): Task[] {
  return tasks.filter((task) => task.date === dateKey);
}

/** The teams of one task, out of a flat list covering several tasks. */
export function teamsOfTask(teams: readonly TaskTeam[], taskId: string): TaskTeam[] {
  return teams.filter((team) => team.taskId === taskId);
}

export interface TaskKpiContext {
  teams?: readonly TaskTeam[];
}

/**
 * Counts one day's board. Statuses are read through `normalizeTaskStatus()`, so
 * a row an older build filed as "ממתינה לשיבוץ" is counted where it belongs
 * today — under בביצוע — instead of falling out of every column.
 */
export function taskDayKpis(
  tasks: readonly Task[],
  context: TaskKpiContext = {},
): TaskDayKpis {
  const teams = context.teams ?? [];
  const kpis: TaskDayKpis = {
    total: tasks.length,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    open: 0,
    urgent: 0,
    unassigned: 0,
  };

  for (const task of tasks) {
    if (isCompletedTask(task)) kpis.completed++;
    if (normalizeTaskStatus(task.statusId) === 'cancelled') kpis.cancelled++;

    if (isActiveTask(task)) {
      kpis.inProgress++;
      kpis.open++;
      if (task.priority === 'urgent') kpis.urgent++;
      if (teamsOfTask(teams, task.id).length === 0) kpis.unassigned++;
    }
  }

  return kpis;
}

// ── Fleet readiness ──────────────────────────────────────────────────

export interface FleetReadiness {
  total: number;
  available: number;
  assigned: number;
  maintenance: number;
  grounded: number;
  /** Share of the fleet that is available right now, 0–100. */
  readyPercent: number;
}

/**
 * The vehicles bound to a team on an active task — what "assigned" means on the
 * readiness widget. A day key narrows it to that day's convoys; without one
 * every active task counts.
 */
export function assignedVehicleIds(
  teams: readonly TaskTeam[],
  tasks: readonly Task[],
  dateKey?: string,
): string[] {
  return [...activeAssignmentIndex(teams, tasks, dateKey).vehicleIds];
}

/**
 * The roster soldiers riding an active task, in any crew role — what the driver
 * widget deducts from "זמינים".
 */
export function assignedSoldierIds(
  teams: readonly TaskTeam[],
  tasks: readonly Task[],
  dateKey?: string,
): string[] {
  return [...activeAssignmentIndex(teams, tasks, dateKey).soldierIds];
}

/**
 * Readiness of the vehicle pool, counted through the *effective* status — so a
 * vehicle out on an active task is משובץ without its own record having been
 * touched, and stands back up by itself the moment the task is closed. A
 * vehicle whose record already says `in_use` counts as assigned too; בטיפול and
 * מושבת are counted first and are never overruled by a convoy.
 */
export function fleetReadiness(
  vehicles: readonly Vehicle[],
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): FleetReadiness {
  const readiness: FleetReadiness = {
    total: vehicles.length,
    available: 0,
    assigned: 0,
    maintenance: 0,
    grounded: 0,
    readyPercent: 0,
  };

  for (const vehicle of vehicles) {
    const status = effectiveVehicleStatus(vehicle, index);
    if (status === 'maintenance') readiness.maintenance++;
    else if (status === 'grounded') readiness.grounded++;
    else if (status === 'in_use') readiness.assigned++;
    else readiness.available++;
  }

  readiness.readyPercent =
    readiness.total === 0 ? 0 : Math.round((readiness.available / readiness.total) * 100);
  return readiness;
}

// ── Drivers ──────────────────────────────────────────────────────────

export interface DriverAvailability {
  total: number;
  available: number;
  onMission: number;
  leave: number;
  unavailable: number;
  /** Of the roster, how many hold a military driving licence. */
  licensed: number;
  /** Licensed and available at the same time — who can actually be sent. */
  readyDrivers: number;
}

/**
 * Availability across the roster, read through the *effective* status: a
 * soldier who is "בבית עד יום שלישי" is counted as unavailable, and one riding
 * an active task is counted as במשימה — in both cases without their stored
 * record being rewritten. A soldier out on a convoy therefore leaves "זמינים"
 * and leaves {@link DriverAvailability.readyDrivers} with it, which is the
 * whole point: the widget answers "who can I send *now*".
 */
export function driverAvailability(
  soldiers: readonly Soldier[],
  now: number = Date.now(),
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): DriverAvailability {
  const counts: DriverAvailability = {
    total: soldiers.length,
    available: 0,
    onMission: 0,
    leave: 0,
    unavailable: 0,
    licensed: 0,
    readyDrivers: 0,
  };

  for (const soldier of soldiers) {
    const state = effectiveSoldierAvailability(soldier, index, now);
    if (state === 'available') counts.available++;
    if (state === 'on_mission') counts.onMission++;
    if (state === 'leave') counts.leave++;
    if (state === 'unavailable') counts.unavailable++;

    const licensed = Boolean(soldier.licenseWeightCategory);
    if (licensed) counts.licensed++;
    if (licensed && state === 'available') counts.readyDrivers++;
  }

  return counts;
}

// ── The ledger, today ────────────────────────────────────────────────

export interface OverrideActivity {
  /** Every ledger row filed on the day. */
  logged: number;
  /** Rows that are a "שבץ בכל זאת" decision. */
  overrides: number;
  /** Of those, the ones taken over a `CRITICAL` finding. */
  critical: number;
  /** The override rows themselves, newest first. */
  entries: AuditLogEntry[];
}

/** The audit rows filed on one calendar day, newest first. */
export function auditLogsOnDay(
  logs: readonly AuditLogEntry[],
  dateKey: string,
): AuditLogEntry[] {
  return logs
    .filter((entry) => toDateKey(Date.parse(entry.createdAt)) === dateKey)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Conflicts a manager took responsibility for today. */
export function overrideActivity(
  logs: readonly AuditLogEntry[],
  dateKey: string,
): OverrideActivity {
  const today = auditLogsOnDay(logs, dateKey);
  const entries = today.filter((entry) => entry.action === 'ASSIGNMENT_OVERRIDE');
  return {
    logged: today.length,
    overrides: entries.length,
    critical: entries.filter((entry) => entry.override?.severity === 'CRITICAL').length,
    entries,
  };
}

// ── Field alerts ─────────────────────────────────────────────────────

export const DASHBOARD_ALERT_TONES = ['critical', 'warning', 'info'] as const;

export type DashboardAlertTone = (typeof DASHBOARD_ALERT_TONES)[number];

/** One line on the "התראות שטח פעילות" panel. */
export interface DashboardAlert {
  id: string;
  tone: DashboardAlertTone;
  label: string;
  count: number;
}

export const DASHBOARD_ALERT_TONE_CLASSES: Record<DashboardAlertTone, string> = {
  critical: 'border-rose-300 bg-rose-50 text-rose-700',
  warning: 'border-amber-300 bg-amber-50 text-amber-800',
  info: 'border-brand-300 bg-brand-50 text-brand-700',
};

// ── The snapshot the screen renders ──────────────────────────────────

export interface DashboardInput {
  tasks: readonly Task[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  teams?: readonly TaskTeam[];
  auditLogs?: readonly AuditLogEntry[];
  now?: number;
}

export interface DashboardSnapshot {
  /** The calendar day every counter on the screen is about. */
  dateKey: string;
  tasks: TaskDayKpis;
  fleet: FleetReadiness;
  drivers: DriverAvailability;
  overrides: OverrideActivity;
  alerts: DashboardAlert[];
  /** Who is out right now — the derivation the fleet and driver widgets share. */
  assignments: AssignmentIndex;
}

/**
 * Everything the command dashboard shows, derived in one pass.
 *
 * The convoy index is built once and handed to both readiness widgets, so a
 * vehicle counted as משובץ and the driver deducted from זמינים are the same
 * assignment read twice, never two rules that can disagree.
 */
export function buildDashboardSnapshot(input: DashboardInput): DashboardSnapshot {
  const now = input.now ?? Date.now();
  const dateKey = toDateKey(now);
  const teams = input.teams ?? [];
  const auditLogs = input.auditLogs ?? [];

  const today = tasksOnDay(input.tasks, dateKey);
  const tasks = taskDayKpis(today, { teams });
  const assignments = activeAssignmentIndex(teams, input.tasks, dateKey);
  const fleet = fleetReadiness(input.vehicles, assignments);
  const drivers = driverAvailability(input.soldiers, now, assignments);
  const overrides = overrideActivity(auditLogs, dateKey);

  const candidates: DashboardAlert[] = [
    { id: 'urgent', tone: 'critical', label: 'משימות דחופות פתוחות היום', count: tasks.urgent },
    { id: 'unassigned', tone: 'warning', label: 'משימות היום ללא רכב וצוות', count: tasks.unassigned },
    { id: 'overrides', tone: 'info', label: 'שיבוצים שבוצעו בכל זאת היום', count: overrides.overrides },
    { id: 'grounded', tone: 'critical', label: 'רכבים מושבתים או בטיפול', count: fleet.grounded + fleet.maintenance },
  ];
  const alerts = candidates.filter((alert) => alert.count > 0);

  return { dateKey, tasks, fleet, drivers, overrides, alerts, assignments };
}
